import { useEffect, useState } from "react";
import { Check, Clock, Mail, Phone, X } from "lucide-react";
import { api, formatDate, money } from "@/lib/api";

export const slaLabel = applied => {
  const h = (Date.now() - Date.parse(applied)) / 36e5;
  if (isNaN(h)) return null;
  const left = 24 - h;
  return left < 0 ? { text: `Overdue by ${Math.round(-left)}h`, tone: "danger" } : { text: `${Math.max(1, Math.round(left))}h left to review`, tone: left < 6 ? "warn" : "" };
};

const Row = ({ label, value }) => <div className="applicant-row"><span>{label}</span><b data-testid={`applicant-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value || "—"}</b></div>;
const PAY = { awaiting_payment: "Awaiting payment", submitted: "Payment submitted · awaiting verification", paid: "Paid & verified", rejected: "Payment rejected" };

export const ApplicantModal = ({ applicantRef, onClose, onReviewed }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api(`/admin/applicants/${applicantRef}`).then(setD).catch(e => setErr(e.message)); }, [applicantRef]);

  const review = async action => {
    setBusy(true); setErr("");
    try { const r = await api(`/admin/applicants/${applicantRef}/review`, { method: "POST", body: JSON.stringify({ action, note }) }); onReviewed(r.message); }
    catch (e) { setErr(e.message); setBusy(false); }
  };
  const a = d?.application || {}; const u = d?.user || {}; const p = d?.payment; const pending = d?.status === "Pending";
  const sla = pending && d?.applied_at ? slaLabel(d.applied_at) : null;

  return (
    <div className="admin-modal" role="dialog" data-testid="applicant-modal">
      <div className="admin-modal-body wide">
        <div className="admin-modal-head">
          <div>
            <span className="eyebrow">Membership applicant</span>
            <h2 data-testid="applicant-name">{u.name || a.name || "Applicant"}</h2>
            <div className="applicant-badges">
              <span className={`pill ${d?.status === "Approved" ? "paid" : d?.status === "Rejected" ? "danger" : ""}`} data-testid="applicant-status">{d?.status || "…"}</span>
              {sla && <span className={`pill ${sla.tone}`} data-testid="applicant-sla"><Clock size={11} /> {sla.text}</span>}
              {d?.applied_at && <small className="hint" style={{ padding: 0 }}>Applied {formatDate(d.applied_at)}</small>}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="applicant-close" aria-label="Close"><X size={18} /></button>
        </div>
        {err && <div className="error-message" data-testid="applicant-error">{err}</div>}
        {!d ? <div className="hint">Loading applicant…</div> : (
          <div className="applicant-grid" data-testid="applicant-details">
            <Row label="Email" value={u.email || a.email} />
            <Row label="Phone" value={u.phone || a.phone} />
            <Row label="Qualification" value={a.qualification || u.qualification} />
            <Row label="College / Institution" value={a.institution || u.institution} />
            <Row label="Year of passing" value={a.passing_year || u.passing_year} />
            <Row label="Organisation" value={a.organization || u.organization} />
            <Row label="City" value={a.city || u.city} />
            <Row label="Plan" value={p?.plan_label || a.plan_key || u.plan_key} />
            <Row label="Payment" value={p ? `${PAY[p.status] || p.status}${p.amount ? ` · ${money(p.amount, p.currency)}` : ""}${p.reference ? ` · Ref ${p.reference}` : ""}` : "No payment record yet"} />
            <Row label="Account" value={u.user_id ? `${(u.auth_providers || []).join(" + ") || "no login yet"} · ${u.status}` : "No account — will be created on approval"} />
            <Row label="Member ID" value={d.member?.member_id} />
            {a.review_note && <Row label="Admin note" value={a.review_note} />}
          </div>
        )}
        {d && (u.email || a.email) && (
          <div className="applicant-contact">
            <a className="link" href={`mailto:${u.email || a.email}`} data-testid="applicant-mail"><Mail size={13} /> Email applicant</a>
            {(u.phone || a.phone) && <a className="link" href={`tel:${u.phone || a.phone}`} data-testid="applicant-call"><Phone size={13} /> Call</a>}
          </div>
        )}
        {pending && rejecting && (
          <label className="applicant-reason">Reason for rejection (sent to the applicant, optional)
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Qualification certificate could not be verified" data-testid="applicant-reject-note" />
          </label>
        )}
        <div className="admin-modal-actions">
          <button type="button" className="button button-ghost dark" onClick={onClose} data-testid="applicant-cancel">Close</button>
          {pending && !rejecting && <button type="button" className="button button-ghost dark danger" disabled={busy} onClick={() => setRejecting(true)} data-testid="applicant-reject-button"><X size={14} /> Reject</button>}
          {pending && rejecting && <button type="button" className="button button-ghost dark danger" disabled={busy} onClick={() => review("reject")} data-testid="applicant-reject-confirm">{busy ? "Rejecting…" : "Confirm rejection"}</button>}
          {pending && <button type="button" className="button button-gold" disabled={busy} onClick={() => review("approve")} data-testid="applicant-approve-button"><Check size={14} /> {busy ? "Approving…" : "Approve membership"}</button>}
        </div>
        {pending && <div className="hint" style={{ marginTop: 10 }}>The applicant is emailed automatically with the decision. Applications should be reviewed within 24 hours.</div>}
      </div>
    </div>
  );
};
